import { useMemo } from 'react';
import styled from 'styled-components';
import { Icon } from '../../../../../components/common/icon/Icon';
import { isDirectDealSliderFilterKey } from '../../../../../constants/filter/slider/directDealFilterSliderConstant';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/store/useStore';
import { IFilter, IFilterOption } from '../../../../../interfaces/Filter.interfaces';
import { filterSlice } from '../../../../../store/filterSlice';
import { formatDirectDealSliderRangeText } from '../../../../../utils/filter/directDealSliderValue.util';
import { isFilterSelectedAsAll } from '../../../../../utils/filter/isFilterSelectedAsAll.util';

const FILTER_TYPE = 'directDealFilter';

interface ISelectedFilterChip {
  key: string;
  label: string;
  filter: IFilter;
  // 옵션 하나만 해제하는 칩일 때의 대상 옵션. 없으면 항목 전체를 기본값으로 되돌린다.
  option?: IFilterOption;
}

export const DirectDealSelectedFilterChipSection = () => {
  const dispatch = useAppDispatch();
  const filterActions = filterSlice.actions;
  const directDealFilters = useAppSelector((state) => state.filter.directDealFilter.globalFilters);

  const chips = useMemo<ISelectedFilterChip[]>(() => {
    const newChips: ISelectedFilterChip[] = [];

    directDealFilters.forEach((filter) => {
      // 정렬은 매물 카운트 영역에서 따로 보여 주므로 칩으로 만들지 않는다.
      if (filter.key === 'sorter') {
        return;
      }

      // 범위 조건은 선택 범위 전체를 칩 1개로 보여 준다.
      if (isDirectDealSliderFilterKey(filter.key)) {
        const isDefaultRange = filter.options.every((option) => option.value === option.defaultValue);

        if (!isDefaultRange) {
          newChips.push({
            key: filter.key,
            label: formatDirectDealSliderRangeText(
              filter.key,
              filter.options.map((option) => Number(option.value)),
            ),
            filter,
          });
        }

        return;
      }

      // 하나도 고르지 않았거나 모두 고른 항목은 '전체'와 같아 칩으로 보여 주지 않는다.
      if (isFilterSelectedAsAll(filter)) {
        return;
      }

      // 판매 상태는 기본값인 '판매 중'만 선택된 경우 칩을 보여 주지 않는다.
      if (filter.key === 'status') {
        const selectedOptions = filter.options.filter((option) => option.isSelected);
        const isDefaultSelection = filter.options.every((option) => option.isSelected === Boolean(option.isDefault));

        if (!isDefaultSelection) {
          newChips.push({ key: filter.key, label: selectedOptions[0].optionName, filter });
        }

        return;
      }

      // 나머지는 선택한 옵션마다 칩 1개씩 보여 준다.
      filter.options
        .filter((option) => option.isSelected)
        .forEach((option) => {
          newChips.push({ key: `${filter.key}-${option.value}`, label: option.optionName, filter, option });
        });
    });

    return newChips;
  }, [directDealFilters]);

  const handleChipClick = (chip: ISelectedFilterChip) => {
    if (chip.option) {
      dispatch(
        filterActions.changeFilterOptionIsSelected({
          filterType: FILTER_TYPE,
          targetFilter: chip.filter,
          targetOption: chip.option,
        }),
      );
      return;
    }

    dispatch(filterActions.resetOneFilter({ filterType: FILTER_TYPE, filter: chip.filter }));
  };

  // 정렬도 필터의 하나로 보아, 조건과 함께 기본값(최신 등록순)으로 되돌린다.
  const handleResetButtonClick = () => {
    dispatch(filterActions.resetAllFilters({ filterType: FILTER_TYPE }));
  };

  if (chips.length === 0) {
    return null;
  }

  return (
    <Container>
      <ResetIconButtonWrapper>
        <ResetIconButton onClick={handleResetButtonClick} aria-label={'재설정'}>
          <ResetIcon />
        </ResetIconButton>
      </ResetIconButtonWrapper>

      <ChipContainer>
        {chips.map((chip) => (
          <FilterChip key={chip.key} onClick={() => handleChipClick(chip)}>
            <FilterChipText>{chip.label}</FilterChipText>
            <Icon name={'CloseGrey'} size={'Small'} />
          </FilterChip>
        ))}

        <ResetTextButton onClick={handleResetButtonClick}>{'처음으로'}</ResetTextButton>
      </ChipContainer>

      {/* 위 브랜드·필터 버튼 줄의 그라데이션을 '매물 n대' 위까지 이어 준다. */}
      <BlurSection />
    </Container>
  );
};

const ResetIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
      <path
        d="M3.971 7.36284L7.429 9.95584C8.076 10.4408 9 9.97884 9 9.16984V3.98384C9 3.17484 8.076 2.71284 7.429 3.19784L3.971 5.79084C3.447 6.18384 3.447 6.96984 3.971 7.36284Z"
        fill="#505363"
      />
      <path
        d="M9.885 5.5625H7C6.448 5.5625 6 6.0105 6 6.5625C6 7.1145 6.448 7.5625 7 7.5625H9.885C11.815 7.5625 13.385 9.1325 13.385 11.0625C13.385 12.9765 11.887 14.5135 9.989 14.5615C9.869 14.5625 7.055 14.5615 7.005 14.5625C7.003 14.5625 7.002 14.5615 7 14.5615C6.448 14.5615 6 15.0095 6 15.5615C6 16.1135 6.448 16.5615 7 16.5615C7 16.5615 9.987 16.5625 10.025 16.5615C13.03 16.4855 15.384 14.0705 15.384 11.0625C15.385 8.0295 12.917 5.5625 9.885 5.5625Z"
        fill="#505363"
      />
    </svg>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  align-items: center;

  padding-bottom: 16px;

  position: relative;
`;

// 브랜드 영역(BrandFilterToggleSection)·필터 버튼 줄(FilterSection)의 그라데이션과 같다.
const BlurSection = styled.div`
  width: 40px;

  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;

  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 64%);

  z-index: 6;
`;

const ResetIconButtonWrapper = styled.div`
  display: flex;
  align-items: center;

  padding: 0px 8px 0px 16px;

  border-right: 1px solid var(--Light-Scheme-Monochrome-grey40, #c6c6d5);
`;

const ResetIconButton = styled.button`
  width: 30px;
  height: 30px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const ChipContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 0 0;
  gap: 8px;

  padding: 0px 16px 0px 8px;

  overflow-x: scroll;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const FilterChip = styled.button`
  height: 30px;

  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;

  padding: 6px 10px;

  border-radius: var(--radius-round, 80px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey40, #c6c6d5);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const FilterChipText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */

  white-space: nowrap;
`;

const ResetTextButton = styled.button`
  height: 22px;

  flex-shrink: 0;

  margin: 0px 10px;
  padding: 2px 0px;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-offblack, #505363);

  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */

  white-space: nowrap;
`;
